import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableCell,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { HighlightOffRounded, Info, Save } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FileNode,
  FilePermissionType,
  ShareFileSystemObjectRequest,
  ShareFileSystemTarget,
} from '../../api-client/autogenerated';
import { getAllProjectUsersState, getProjectState } from '../../features/project/selectors';
import { getGroupsState } from '../../features/groups/selector';
import {
  getCurrentSecurityGroup,
  getSubscriberSecurityGroupState,
} from '../../features/security/selectors';
import { getUserState } from '../../features/user/selectors';
import { shareMultipleUserFileSystemObject } from '../../models/api/filesystem';
import { isPublicPage } from '../../scripts/utils';
import { CancelButton, SubmitButton } from '../custom-components/CustomButtons';
import CircularLoader from '../loader/CircularLoader';
import { RedCross } from '../custom-components/CustomCheckboxes';
import {
  canManageDriveAccess,
  combineWithGroupPermissions,
  deriveDriveAccessGroups,
  deriveDriveAccessRows,
  DriveAccessGroupRow,
  DriveAccessRow,
  DrivePermissionTriple,
} from './drive-access-utils';
import { PermissionActionType, PermissionsAndAction } from './ManagePermissionsDialog';
import PermissionsDialogRow from './PermissionsDialogRow';
import PermissionTag, { PermissionTagType } from './PermissionTag';

interface DriveAccessDialogProps {
  open: boolean;
  file?: FileNode;
  closeDialog: () => void;
  onFileUpdated: (node: FileNode) => void;
}

const useStyles = makeStyles((theme) => ({
  /**
   * A fixed panel that still has to fit the screen. A phone held sideways is wide enough to miss
   * the xs breakpoint but only ~390px tall, so a flat 720px cap let the paper grow past the
   * viewport and take its own title and Save row with it. Bounded by what the viewport can hold,
   * MUI's own scroller keeps those two in place and the roster scrolls between them.
   */
  container: {
    minWidth: 'min(650px, calc(100% - 64px))',
    maxHeight: 'min(720px, calc(100% - 64px))',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      minWidth: '100%',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #EDECEC',
  },
  title: {
    marginTop: '3px',
    marginLeft: '6px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    textTransform: 'none',
  },
  subtitle: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '18px',
    lineHeight: '21px',
    textAlign: 'left',
    textTransform: 'none',
    color: '#464546',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  sectionCaption: {
    color: '#7A797A',
    marginBottom: 8,
  },
  /**
   * Only enough to sit the icon on the text's line. No type or colour of its own: the source reads
   * as one column, so "Parent Folder" has to look exactly like "Creator" and "Security Group"
   * beside it.
   */
  accessSource: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  /**
   * Only the automatic section names a source, but every section reserves the column at the same
   * width: the three tables are siblings, and without it their checkbox columns land in
   * different places down the dialog.
   */
  accessSourceCell: {
    width: 190,
    // The table is padding="none" so the checkbox columns can be pinned; the two text columns get
    // their breathing room back here instead of running into each other.
    paddingRight: 16,
  },
  /** Same reasoning as accessSourceCell, one column over: pins the name column so the three
   * sibling tables' permission checkboxes line up regardless of whose names are in each one. */
  nameCell: {
    width: 240,
    // Without this the access source starts flush against the end of the name's chip.
    paddingRight: 24,
  },
  accessSourceInfoIcon: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 18,
    flexShrink: 0,
    marginLeft: 6,
  },
}));

/**
 * Define Access for CENTERLINE Drive: the whole project roster in three sections - access that
 * arrives on its own (Drive admins, and anyone a parent folder already reaches), the users and
 * groups eligible to be given access here, and the users whose security group rules them out.
 * Every section shows the permission matrix; only the middle one is editable. Access from
 * legacy link or external email shares stays in effect server-side and is not shown here.
 * Editable by the item's owner, Drive admins, and holders of an Admin share on it or a parent.
 */
export default function DriveAccessDialog(props: DriveAccessDialogProps) {
  const { open, file, closeDialog, onFileUpdated } = props;
  const classes = useStyles();

  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const allProjectUsers = useSelector(getAllProjectUsersState);
  const securityGroups = useSelector(getSubscriberSecurityGroupState);
  const security = useSelector(getCurrentSecurityGroup);
  const groups = useSelector(getGroupsState);

  const [permissionsByUserId, setPermissionsByUserId] = useState<
    Record<string, PermissionsAndAction>
  >({});
  const [permissionsByGroupId, setPermissionsByGroupId] = useState<
    Record<string, PermissionsAndAction>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const rows = useMemo(
    () => deriveDriveAccessRows(file, allProjectUsers, securityGroups, project, user.id),
    [file, allProjectUsers, securityGroups, project, user.id],
  );
  const groupRows = useMemo(() => deriveDriveAccessGroups(file, groups), [file, groups]);

  const editable = !isPublicPage() && canManageDriveAccess(file, user, security, project, groups);

  const automaticRows = rows.filter((row) => row.section === 'automatic');
  const grantableRows = rows.filter((row) => row.section === 'grantable');
  const noAccessRows = rows.filter((row) => row.section === 'none');

  // Checkbox state for the editable section, seeded from the direct shares on this node
  // whenever the dialog (re)opens.
  useEffect(() => {
    if (!open) return;
    const seededUsers: Record<string, PermissionsAndAction> = {};
    rows.forEach((row) => {
      if (row.section !== 'grantable') return;
      seededUsers[row.userId] = {
        action: PermissionActionType.None,
        read: !!row.direct?.read,
        write: !!row.direct?.write,
        admin: !!row.direct?.admin,
        userId: row.userId,
        shareable: true,
      };
    });
    const seededGroups: Record<string, PermissionsAndAction> = {};
    groupRows.forEach((groupRow) => {
      seededGroups[groupRow.groupId] = {
        action: PermissionActionType.None,
        read: !!groupRow.direct?.read,
        write: !!groupRow.direct?.write,
        admin: !!groupRow.direct?.admin,
        group: groups.find((g) => g.id === groupRow.groupId),
        shareable: true,
      };
    });
    setPermissionsByUserId(seededUsers);
    setPermissionsByGroupId(seededGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  const handleEditPermission = (permission: PermissionsAndAction) => {
    if (permission.group) {
      setPermissionsByGroupId((prev) => ({ ...prev, [permission.group!.id]: permission }));
      return;
    }
    if (!permission.userId) return;
    setPermissionsByUserId((prev) => ({ ...prev, [permission.userId!]: permission }));
  };

  const groupPermissionTriples = useMemo(() => {
    const triples: Record<string, DrivePermissionTriple> = {};
    Object.entries(permissionsByGroupId).forEach(([groupId, p]) => {
      triples[groupId] = { read: p.read, write: p.write, admin: p.admin };
    });
    return triples;
  }, [permissionsByGroupId]);

  const getFilePermissions = (p: PermissionsAndAction) => {
    const permissions = [];
    if (p.read) permissions.push(FilePermissionType.Read);
    if (p.write) permissions.push(FilePermissionType.Modify);
    if (p.admin) permissions.push(FilePermissionType.Delete);
    return permissions;
  };

  const submitPermissionChanges = async () => {
    if (!file || !project) return;
    const hadDirect = new Set(rows.filter((row) => row.direct).map((row) => row.userId));
    const groupHadDirect = new Set(
      groupRows.filter((groupRow) => groupRow.direct).map((groupRow) => groupRow.groupId),
    );
    // A row cleared to nothing is an unshare, which only means something for a target that
    // actually had a share on this node.
    const requests: ShareFileSystemObjectRequest[] = [
      ...Object.values(permissionsByUserId)
        .filter((p) => p.action !== PermissionActionType.None)
        .filter((p) => p.read || p.write || p.admin || hadDirect.has(p.userId!))
        .map((p) => ({
          fileNodeKey: file.fullKey,
          targetType: ShareFileSystemTarget.ProjectUsers,
          filePermissions: getFilePermissions(p),
          target: [p.userId!],
          creatorUserId: file.ownerId,
        })),
      ...Object.values(permissionsByGroupId)
        .filter((p) => p.action !== PermissionActionType.None)
        .filter((p) => p.read || p.write || p.admin || groupHadDirect.has(p.group!.id))
        .map((p) => ({
          fileNodeKey: file.fullKey,
          targetType: ShareFileSystemTarget.UserGroup,
          filePermissions: getFilePermissions(p),
          target: [p.group!.id],
          creatorUserId: file.ownerId,
        })),
    ];
    if (!requests.length) {
      closeDialog();
      return;
    }
    setSubmitting(true);
    try {
      const newFiles = await shareMultipleUserFileSystemObject(project.id, requests);
      const latest = newFiles[newFiles.length - 1];
      if (latest) onFileUpdated(latest);
    } catch (e: any) {
      console.log(e);
    } finally {
      setSubmitting(false);
      closeDialog();
    }
  };

  const permissionRowFor = (row: DriveAccessRow): PermissionsAndAction => {
    if (row.section === 'grantable') {
      const own = permissionsByUserId[row.userId];
      if (!own)
        return {
          action: PermissionActionType.None,
          read: false,
          write: false,
          admin: false,
          userId: row.userId,
          shareable: true,
        };
      // A group grant cannot be revoked on the member's own row, so the row shows whichever of
      // the two is higher.
      const effective = combineWithGroupPermissions(
        { read: own.read, write: own.write, admin: own.admin },
        row.userId,
        groups,
        groupPermissionTriples,
      );
      return { ...own, ...effective };
    }
    // Automatic rows report what the user already holds: everything for the creator and for an
    // admin, the inherited permissions for someone a parent folder reaches. The no-access
    // section shows its own crossed-out cells and never reads these values.
    const holdsEverything = row.automaticReason === 'admin' || row.automaticReason === 'creator';
    const inherited = row.inheritedFrom.flatMap((entry) => entry.permissions);
    return {
      action: PermissionActionType.None,
      read: holdsEverything || inherited.includes(FilePermissionType.Read),
      write: holdsEverything || inherited.includes(FilePermissionType.Modify),
      admin: holdsEverything || inherited.includes(FilePermissionType.Delete),
      userId: row.userId,
      shareable: true,
      parentFolder: row.inheritedFrom[0]?.folderName,
    };
  };

  const permissionHeader = (showAccessSource: boolean) => (
    <TableHead>
      <TableRow style={{ backgroundColor: 'white' }}>
        <TableCell style={{ backgroundColor: 'white' }} />
        <TableCell className={classes.nameCell} style={{ backgroundColor: 'white' }} />
        <TableCell className={classes.accessSourceCell} style={{ backgroundColor: 'white' }}>
          {showAccessSource ? 'Access Source' : ''}
        </TableCell>
        <TableCell align="center" style={{ backgroundColor: 'white' }}>
          View
        </TableCell>
        <TableCell align="center" style={{ backgroundColor: 'white' }}>
          Create
        </TableCell>
        <TableCell align="center" style={{ backgroundColor: 'white' }}>
          Admin
        </TableCell>
      </TableRow>
    </TableHead>
  );

  /**
   * Why this user already has access, in the order deriveDriveAccessRows settles it: the item's
   * creator, then a Drive-admin security group, then the parent folder whose share reaches here.
   * Only the automatic section renders this - the other two have no source to name.
   */
  const accessSourceFor = (row: DriveAccessRow) => {
    if (row.automaticReason === 'creator') return <span>Creator</span>;
    if (row.automaticReason === 'admin') return <span>Security Group</span>;
    // The share carries the folder's stored filename, which ends in the separator every folder
    // key does. Nobody needs that path in the table itself, so it only surfaces in the tooltip.
    const folderName = row.inheritedFrom[0]?.folderName?.replace(/\/$/, '');
    if (!folderName) return null;
    return (
      <Tooltip arrow placement="top" title={folderName}>
        <span className={classes.accessSource}>
          Parent Folder
          <Info className={classes.accessSourceInfoIcon} />
        </span>
      </Tooltip>
    );
  };

  /** The no-access rows carry a fixed, unreachable state, so they get their own cells. */
  const renderNoAccessRow = (row: DriveAccessRow, index: number) => (
    <TableRow key={row.userId} style={{ backgroundColor: index % 2 === 0 ? '#F9F9F9' : 'white' }}>
      <TableCell style={{ width: 24 }} />
      <TableCell className={classes.nameCell}>
        <PermissionTag label={row.name} type={PermissionTagType.Person} disableDelete />
      </TableCell>
      <TableCell className={classes.accessSourceCell} />
      {[0, 1, 2].map((cell) => (
        <TableCell key={cell} align="center">
          <RedCross />
        </TableCell>
      ))}
    </TableRow>
  );

  const renderSection = (
    title: string,
    caption: string | null,
    sectionRows: DriveAccessRow[],
    options: {
      rowsEditable?: boolean;
      noAccess?: boolean;
      groupRowsToRender?: DriveAccessGroupRow[];
      showAccessSource?: boolean;
    } = {},
  ) => {
    const {
      rowsEditable = false,
      noAccess = false,
      groupRowsToRender = [],
      showAccessSource = false,
    } = options;
    if (!sectionRows.length && !groupRowsToRender.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <Typography className={classes.subtitle} style={{ marginBottom: 4 }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="body2" className={classes.sectionCaption}>
            {caption}
          </Typography>
        )}
        <Table size="small" padding="none">
          {permissionHeader(showAccessSource)}
          <TableBody>
            {/* Groups lead the eligible section: a grant here reaches every member. */}
            {groupRowsToRender.map((groupRow, index) => (
              <PermissionsDialogRow
                key={groupRow.groupId}
                permissionRow={permissionsByGroupId[groupRow.groupId]}
                index={index}
                label={groupRow.name}
                type={PermissionTagType.Group}
                users={groupRow.members}
                handleEditPermission={rowsEditable ? handleEditPermission : undefined}
                disablePermissions={!rowsEditable}
                accessSource={null}
                accessSourceCellClassName={classes.accessSourceCell}
                nameCellClassName={classes.nameCell}
              />
            ))}
            {sectionRows.map((row, index) =>
              noAccess ? (
                renderNoAccessRow(row, index + groupRowsToRender.length)
              ) : (
                <PermissionsDialogRow
                  key={row.userId}
                  permissionRow={permissionRowFor(row)}
                  index={index + groupRowsToRender.length}
                  label={row.name}
                  type={PermissionTagType.Person}
                  handleEditPermission={rowsEditable ? handleEditPermission : undefined}
                  disablePermissions={!rowsEditable || row.isOwner || row.isSelf}
                  accessSource={showAccessSource ? accessSourceFor(row) : null}
                  accessSourceCellClassName={classes.accessSourceCell}
                  nameCellClassName={classes.nameCell}
                />
              ),
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      onContextMenu={(e) => e.stopPropagation()}
      maxWidth="md"
      PaperProps={{ className: classes.container }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.title} align="left">
          {editable ? 'Set User Access and Permissions' : 'Current Access and Permissions'}
        </Typography>
        {file && (
          <Typography variant="body2" style={{ marginLeft: 6, color: '#464546' }}>
            {file.relativeKey || file.fullKey}
          </Typography>
        )}
        <IconButton
          style={{ right: '20px', top: '12px', position: 'absolute' }}
          onClick={closeDialog}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 0, paddingBottom: 16 }}>
        {!editable && (
          <Typography
            variant="body2"
            style={{ fontSize: '16px', fontWeight: 500, margin: '16px 0px', textAlign: 'center' }}
          >
            Access can be changed by the item&apos;s owner, CENTERLINE Drive admins, and users with
            Admin access on it or a parent folder.
          </Typography>
        )}
        {renderSection(
          'Automatically Assigned Users',
          'These users are automatically assigned access, either by security group or at the parent/root folder level',
          automaticRows,
          { showAccessSource: true },
        )}
        {renderSection(
          'Project Users Eligible For Access',
          'These users have security group permissions which make them eligible to be given access to files or folders',
          grantableRows,
          {
            rowsEditable: editable,
            groupRowsToRender: groupRows,
          },
        )}
        {renderSection(
          'No CENTERLINE Drive Access',
          'CENTERLINE Drive security group setting for these users is currently set to None -- Minimum View permission is required',
          noAccessRows,
          { noAccess: true },
        )}
        <Typography variant="body2" className={classes.sectionCaption}>
          Access granted through a shared link or to an external email address stays in effect and
          is not shown here.
        </Typography>
      </DialogContent>
      <DialogActions style={{ display: 'flex', justifyContent: 'space-between', padding: 16 }}>
        {!submitting ? (
          <>
            {editable && (
              <SubmitButton
                onClick={submitPermissionChanges}
                startIcon={<Save />}
                style={{ paddingLeft: 16, paddingRight: 16 }}
              >
                Save
              </SubmitButton>
            )}
            <CancelButton onClick={closeDialog} />
          </>
        ) : (
          <CircularLoader fullWidth />
        )}
      </DialogActions>
    </Dialog>
  );
}
